import { FC, useState } from "react";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface DeleteCommunicationPreferenceOptionModalProps {
  name: string;
  subscriberCount?: number;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
}

export const DeleteCommunicationPreferenceOptionModal: FC<DeleteCommunicationPreferenceOptionModalProps> = ({
  name,
  subscriberCount,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    const isDeleted = await onConfirm().finally(() => setIsDeleting(false));
    if (isDeleted) {
      onClose();
    }
  };

  return (
    <DashboardDialog open onClose={onClose} vibe="warning" maxWidth="xs">
      <DashboardDialogBody
        slots={{
          header: (
            <DashboardDialogHeader title={t("dashboard", "communicationPreferences.deleteOption.title", { name })} />
          ),
          content: (
            <DashboardDialogContent>
              <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ whiteSpace: "pre-line" }}>
                {t("dashboard", "communicationPreferences.deleteOption.description", { count: subscriberCount })}
              </DashboardTypography>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                onClick={() => void handleConfirm()}
                isLoading={isDeleting}
                data-test="communication-preference-option-delete-confirm"
              >
                {t("dashboard", "communicationPreferences.deleteOption.submit")}
              </DashboardButton>
              <DashboardButton onClick={onClose}>{t("dashboard", "common.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
